import React from 'react';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { C } from '../../lib/palette';

// An original set of nine small keepsakes that unlock one at a time as
// consistency milestones are hit, building toward a complete set. Each is a
// hand-built mini illustration — not a reskin of a single asset.
const ITEMS = [
  {
    id: 'sprout', tint: '#E1EAE5',
    art: (
      <g>
        <path d="M22 30 L22 18" stroke="#3B7E67" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M22 22 q -8 -2 -11 -9 q 8 0 11 6 z" fill="#62B497" />
        <path d="M22 20 q 8 -2 11 -9 q -8 0 -11 6 z" fill="#8FD3B8" />
      </g>
    ),
  },
  {
    id: 'dew', tint: '#DCEAF2',
    art: (
      <g>
        <path d="M22 8 C 13 20 14 32 22 32 C 30 32 31 20 22 8 Z" fill="#5FA8C9" />
        <ellipse cx="18" cy="24" rx="2.4" ry="4" fill="#CFEAF5" opacity="0.8" />
      </g>
    ),
  },
  {
    id: 'sun', tint: '#F5ECD8',
    art: (
      <g stroke="#B98B3A" strokeWidth="2" strokeLinecap="round">
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
          <line key={a} x1="22" y1="22" x2={22 + 15 * Math.cos((a * Math.PI) / 180)} y2={22 + 15 * Math.sin((a * Math.PI) / 180)} />
        ))}
        <circle cx="22" cy="22" r="7" fill="#E0B24E" stroke="none" />
      </g>
    ),
  },
  {
    id: 'moon', tint: '#ECE4EC',
    art: (
      <g>
        <path d="M28 10 A 13 13 0 1 0 28 34 A 10 10 0 1 1 28 10 Z" fill="#6B4A63" />
        <circle cx="30" cy="15" r="1.6" fill="#C9B3C6" />
      </g>
    ),
  },
  {
    id: 'pebbles', tint: '#E8EAE4',
    art: (
      <g>
        <ellipse cx="22" cy="31" rx="13" ry="5" fill="#9AA39A" />
        <ellipse cx="22" cy="23" rx="9" ry="4.4" fill="#C2C9C0" />
        <ellipse cx="22" cy="16" rx="5.5" ry="3.6" fill="#7E8A7F" />
      </g>
    ),
  },
  {
    id: 'feather', tint: '#E1EAE5',
    art: (
      <g>
        <path d="M14 32 C 20 14 30 8 32 10 C 34 20 26 30 14 32 Z" fill="#4F9A80" />
        <path d="M14 32 L30 12" stroke="#B98B3A" strokeWidth="1.6" strokeLinecap="round" />
      </g>
    ),
  },
  {
    id: 'flower', tint: '#F6E6EC',
    art: (
      <g>
        {[0, 72, 144, 216, 288].map((a) => (
          <ellipse key={a} cx="22" cy="14" rx="4" ry="7" fill="#E7A6B7" transform={`rotate(${a} 22 22)`} />
        ))}
        <circle cx="22" cy="22" r="4" fill="#B98B3A" />
      </g>
    ),
  },
  {
    id: 'mountain', tint: '#E1EAE5',
    art: (
      <g>
        <path d="M6 32 L18 14 L26 24 L31 17 L38 32 Z" fill="#2C5A4E" />
        <path d="M18 14 L14 20 L22 20 Z" fill="#E6EFEA" />
      </g>
    ),
  },
  {
    id: 'star', tint: '#F5ECD8',
    art: (
      <path d="M22 6 L26 17 L38 17 L28 24 L32 36 L22 29 L12 36 L16 24 L6 17 L18 17 Z" fill="#D9A93F" />
    ),
  },
];

export default function CollectionBoard({ activeCount = 0, dormant = false, className = '' }) {
  const unlocked = Math.min(ITEMS.length, activeCount);
  const complete = unlocked >= ITEMS.length;

  return (
    <div className={className} data-testid="metaphor-collection" data-unlocked={unlocked} data-dormant={dormant ? '1' : '0'}>
      <div className="grid grid-cols-3 gap-2.5 p-3">
        {ITEMS.map((it, i) => {
          const on = i < unlocked;
          return (
            <motion.div
              key={it.id}
              data-testid={`collection-item-${it.id}`}
              data-unlocked={on ? '1' : '0'}
              initial={false}
              animate={on ? { scale: [0.7, 1.08, 1], opacity: 1 } : { scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="relative flex aspect-square items-center justify-center rounded-xl"
              style={{
                background: on ? it.tint : '#EEF1EC',
                border: `1px solid ${on ? 'transparent' : C.lineSoft}`,
                boxShadow: on ? '0 1px 6px rgba(44,90,78,0.12)' : 'none',
              }}
            >
              {on ? (
                <svg viewBox="0 0 44 44" width="64%" height="64%">{it.art}</svg>
              ) : (
                <Lock size={16} style={{ color: C.line }} />
              )}
            </motion.div>
          );
        })}
      </div>
      {complete && !dormant && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="pb-1 text-center text-xs font-semibold"
          style={{ color: C.gold }}
          data-testid="collection-complete"
        >
          ✦ A complete set — nicely done
        </motion.p>
      )}
    </div>
  );
}
